import { isDefined } from "@clipboard-health/util-ts";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import {
  LiveActivity,
  type LiveActivityPluginListener,
} from "@clipboardhealth/capacitor-live-activity";
import { useEffect, useRef } from "react";

import { APP_V2_APP_EVENTS, logError } from "../lib/analytics";
import { isIosPlatform } from "../lib/utils";
import { useCreateOrUpdateLiveActivity } from "./api/useCreateOrUpdateLiveActivity";
import { getAttributesFromLiveActivityId } from "./getAttributesFromLiveActivityId";

/**
 * Hook that listens for live activity push token events and registers them with the backend
 * The plugin will handle graceful return in case the device is not valid for live activities
 */
export function useRegisterLiveActivityListener() {
  const { mutateAsync: createOrUpdateLiveActivity } = useCreateOrUpdateLiveActivity();
  const listenerRef = useRef<LiveActivityPluginListener | undefined>(undefined);

  useEffect(() => {
    // Set up listener for live activity push token events
    const setupListener = async () => {
      if (isDefined(listenerRef.current) || !isIosPlatform()) {
        return;
      }

      try {
        const listener = await LiveActivity.addListener("liveActivityPushToken", async (event) => {
          const { activityId, pushToken } = event;
          const { shiftId, workerId } = getAttributesFromLiveActivityId(activityId);

          try {
            // Register the token with the backend, the token may change over time, so we need to update the activity with the new token
            await createOrUpdateLiveActivity({
              data: {
                type: "liveActivity" as const,
                attributes: {
                  activityId,
                  platform: "ios" as const,
                  activityPushToken: pushToken,
                  status: "active" as const,
                  shiftId,
                  workerId,
                },
              },
            });
          } catch (error) {
            logError(APP_V2_APP_EVENTS.LIVE_ACTIVITY_CREATE_OR_UPDATE_ERROR, {
              error,
              metadata: {
                shiftId,
                workerId,
                activityId,
                hasPushToken: isDefined(pushToken),
              },
            });
          }
        });

        listenerRef.current = listener;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.LIVE_ACTIVITY_SET_UP_LISTENER_ERROR, {
          error,
        });
      }
    };

    void setupListener();

    return () => {
      listenerRef.current?.remove();
      listenerRef.current = undefined;
    };
  }, [createOrUpdateLiveActivity]);
}
